import React, { useEffect, useState, useCallback } from 'react';
import {
  Autocomplete,
  TextField,
  CircularProgress,
  Box,
  Typography,
  FormHelperText,
} from '@mui/material';
import projectService from '../../features/projects/services/projectService';
import { logger } from '../../utils/logger';

const ProjectSelect = ({
  value,
  onChange,
  clientId,
  error = false,
  helperText,
  required = false,
  disabled = false,
  label = 'Έργο',
  placeholder = 'Επιλέξτε έργο...',
  size = 'medium',
  name,
  limit = 100,
  ...props
}) => {
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [inputValue, setInputValue] = useState('');
  const [selectedProject, setSelectedProject] = useState(null);
  const loadProjects = useCallback(
    async (searchTerm = '') => {
      if (!clientId) {
        logger.debug('ProjectSelect: no clientId, clearing options');
        setOptions([]);
        return;
      }

      try {
        logger.debug('ProjectSelect: loading', { searchTerm, clientId });
        setLoading(true);
        const params = {
          skip: 0,
          limit,
          client_id: clientId,
        };
        if (searchTerm && searchTerm.trim()) {
          params.search = searchTerm.trim();
        }

        const response = await projectService.getAll(params);
        if (response.success) {
          const data = response.data || [];
          logger.debug('ProjectSelect: loaded projects', { count: data.length });
          setOptions(data);
        } else {
          logger.error('ProjectSelect: API error', response.error);
          setOptions([]);
        }
      } catch (err) {
        logger.error('ProjectSelect: error loading projects', err);
        setOptions([]);
      } finally {
        setLoading(false);
      }
    },
    [clientId, limit],
  );

  useEffect(() => {
    if (!clientId) {
      logger.debug('ProjectSelect: client cleared');
      setOptions([]);
      return;
    }
    loadProjects('');
  }, [clientId, loadProjects]);

  useEffect(() => {
    const timer = setTimeout(() => {
      if (clientId) {
        loadProjects(inputValue);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [inputValue, clientId, loadProjects]);

  useEffect(() => {
    if (!value && value !== 0) {
      logger.debug('ProjectSelect: value cleared');
      setSelectedProject(null);
      return;
    }
    const numericId = Number(value);
    if (!Number.isFinite(numericId)) {
      logger.debug('ProjectSelect: value not numeric', { value });
      setSelectedProject(null);
      return;
    }
    const match = options.find((project) => Number(project.id) === numericId);
    if (match) {
      logger.debug('ProjectSelect: matched project in options', { id: match.id });
      setSelectedProject(match);
    } else if (clientId) {
      logger.debug('ProjectSelect: fetching project by id', { numericId });
      projectService
        .getById(numericId)
        .then((response) => {
          if (response.success && response.data && response.data.client_id === clientId) {
            logger.debug('ProjectSelect: fetched project', response.data);
            setOptions((prev) => {
              const exists = prev.some((project) => Number(project.id) === numericId);
              if (exists) {
                return prev;
              }
              return [response.data, ...prev].slice(0, limit);
            });
            setSelectedProject(response.data);
          }
        })
        .catch((err) => {
          logger.error('ProjectSelect: error fetching project by id', err);
        });
    }
  }, [value, options, clientId, limit]);

  const handleChange = (event, newValue) => {
    logger.debug('ProjectSelect: handleChange', { selectedId: newValue?.id || '' });
    setSelectedProject(newValue);
    if (onChange) {
      const selectedId =
        newValue && newValue.id !== undefined && newValue.id !== null
          ? Number(newValue.id)
          : '';
      if (name) {
        onChange(name, selectedId);
      } else {
        onChange(selectedId);
      }
    }
  };

  return (
    <Box>
      <Autocomplete
        options={options}
        value={selectedProject}
        onChange={handleChange}
        onInputChange={(event, newInputValue) => setInputValue(newInputValue)}
        loading={loading}
        disabled={disabled || !clientId}
        getOptionLabel={(option) => option?.name || ''}
        isOptionEqualToValue={(option, value) => Number(option?.id) === Number(value?.id)}
        renderOption={(props, option) => (
          <Box component="li" {...props} key={option.id}>
            <Box>
              <Typography variant="body1">{option.name}</Typography>
              {option.domain && (
                <Typography variant="caption" color="text.secondary">
                  {option.domain}
                </Typography>
              )}
            </Box>
          </Box>
        )}
        renderInput={(params) => (
          <TextField
            {...params}
            label={label}
            placeholder={clientId ? placeholder : 'Επιλέξτε πρώτα πελάτη'}
            required={required}
            error={error}
            size={size}
            InputProps={{
              ...params.InputProps,
              endAdornment: (
                <>
                  {loading ? <CircularProgress color="inherit" size={20} /> : null}
                  {params.InputProps.endAdornment}
                </>
              ),
            }}
          />
        )}
        noOptionsText={clientId ? 'Δεν βρέθηκαν έργα' : 'Επιλέξτε πελάτη'}
        loadingText="Φόρτωση..."
        {...props}
      />
      {helperText && (
        <FormHelperText error={error} sx={{ mt: 0.5, mx: 1.75 }}>
          {helperText}
        </FormHelperText>
      )}
    </Box>
  );
};

export default ProjectSelect;

